// ============================================================
// loginApp.js — Gestionnaire d'authentification personnalisé
// Deux méthodes de connexion :
//   1. Email + mot de passe → ROPG (appel direct /oauth/token)
//   2. Google → Authorization Code + PKCE (redirection OAuth)
//      Le retour de redirection est détecté au montage via useEffect.
// ============================================================

import React, { useState, useCallback, useEffect } from 'react';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import App from './App';

const DOMAIN      = process.env.REACT_APP_AUTH0_DOMAIN;
const CLIENT_ID   = process.env.REACT_APP_AUTH0_CLIENT_ID;
const AUDIENCE    = 'https://baptistevalette.eu.auth0.com/api/v2/';
const STORAGE_KEY = 'avalist_auth';
const REDIRECT_URI = window.location.origin + '/avalist/';

// ── Helpers PKCE ──────────────────────────────────────────────

function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
    const data   = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// ── Helpers session ───────────────────────────────────────────

function loadStoredAuth() {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        const data = JSON.parse(stored);
        if (data.expiresAt > Date.now()) return data;
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
    return null;
}

function extractRole(accessToken) {
    try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const roles = payload['https://avalist/roles'] || [];
        return roles[0] ? roles[0].toLowerCase() : null;
    } catch (_) {
        return null;
    }
}

// Extrait l'email depuis la réponse token : lit l'id_token en priorité
// (l'email est absent de l'access_token Auth0 avec une audience custom)
function extractEmail(tokenResponse, knownEmail) {
    if (knownEmail) return knownEmail;
    try {
        const token   = tokenResponse.id_token || tokenResponse.access_token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email || null;
    } catch (_) {
        return null;
    }
}

function saveAuth(tokenResponse, knownEmail) {
    const auth = {
        accessToken : tokenResponse.access_token,
        expiresAt   : Date.now() + tokenResponse.expires_in * 1000,
        role        : extractRole(tokenResponse.access_token),
        email       : extractEmail(tokenResponse, knownEmail),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    return auth;
}

// ── Composant ─────────────────────────────────────────────────

// Extrait le token d'invitation depuis l'URL (?invite=TOKEN)
function getInviteToken() {
    var params = new URLSearchParams(window.location.search);
    return params.get('invite') || null;
}

const LoginApp = () => {
    const [authData,           setAuthData]           = useState(loadStoredAuth);
    const [processingCallback, setProcessingCallback] = useState(false);
    const [callbackError,      setCallbackError]      = useState('');
    const [inviteToken,        setInviteToken]        = useState(getInviteToken);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    // Détecte le retour OAuth de Google (URL contient ?code=...&state=...)
    useEffect(() => {
        const params    = new URLSearchParams(window.location.search);
        const code      = params.get('code');
        const state     = params.get('state');
        if (!code) return;

        const storedState  = sessionStorage.getItem('pkce_state');
        const codeVerifier = sessionStorage.getItem('pkce_verifier');

        // Nettoie l'URL immédiatement (le code est à usage unique)
        window.history.replaceState({}, document.title, window.location.pathname);

        if (state !== storedState || !codeVerifier) {
            setCallbackError('Retour OAuth invalide. Veuillez réessayer.');
            return;
        }

        sessionStorage.removeItem('pkce_state');
        sessionStorage.removeItem('pkce_verifier');

        setProcessingCallback(true);
        fetch(`https://${DOMAIN}/oauth/token`, {
            method  : 'POST',
            headers : { 'Content-Type': 'application/json' },
            body    : JSON.stringify({
                grant_type    : 'authorization_code',
                client_id     : CLIENT_ID,
                code          : code,
                redirect_uri  : REDIRECT_URI,
                code_verifier : codeVerifier,
            }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) throw new Error(data.error_description || data.error);
                setAuthData(saveAuth(data));
            })
            .catch(err => setCallbackError(err.message || 'Erreur lors de la connexion Google.'))
            .finally(() => setProcessingCallback(false));
    }, []);

    // Méthode 1 : email + mot de passe (ROPG)
    const handleLogin = useCallback(async (email, password) => {
        const response = await fetch(`https://${DOMAIN}/oauth/token`, {
            method  : 'POST',
            headers : { 'Content-Type': 'application/json' },
            body    : JSON.stringify({
                grant_type : 'password',
                username   : email,
                password   : password,
                audience   : AUDIENCE,
                scope      : 'openid profile email',
                client_id  : CLIENT_ID,
                connection : 'Username-Password-Authentication',
            }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error_description || 'Identifiants incorrects');
        setAuthData(saveAuth(data, email));
    }, []);

    // Méthode 2 : Google (Authorization Code + PKCE → redirection)
    const handleGoogleLogin = useCallback(async () => {
        const verifier  = generateCodeVerifier();
        const challenge = await generateCodeChallenge(verifier);
        const state     = crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

        sessionStorage.setItem('pkce_verifier', verifier);
        sessionStorage.setItem('pkce_state', state);

        const params = new URLSearchParams({
            response_type         : 'code',
            client_id             : CLIENT_ID,
            connection            : 'google-oauth2',
            redirect_uri          : REDIRECT_URI,
            scope                 : 'openid profile email',
            audience              : AUDIENCE,
            state                 : state,
            code_challenge        : challenge,
            code_challenge_method : 'S256',
        });

        window.location.href = `https://${DOMAIN}/authorize?${params}`;
    }, []);

    // Déconnexion
    const handleLogout = useCallback(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthData(null);
    }, []);

    // Lien d'invitation → page de création de compte
    if (inviteToken) {
        return (
            <RegisterPage
                inviteToken={inviteToken}
                onSuccess={function() {
                    // Nettoie l'URL et retourne sur la page de login
                    window.history.replaceState({}, document.title, window.location.pathname);
                    setInviteToken(null);
                }}
            />
        );
    }

    if (authData) {
        return (
            <App
                user={null}
                auth0token={Promise.resolve(authData.accessToken)}
                onLogout={handleLogout}
                role={authData.role}
                userEmail={authData.email}
            />
        );
    }

    if (showForgotPassword) {
        return (
            <ForgotPasswordPage onBack={() => setShowForgotPassword(false)} />
        );
    }

    return (
        <LoginPage
            onLogin={handleLogin}
            onGoogleLogin={handleGoogleLogin}
            processingCallback={processingCallback}
            callbackError={callbackError}
            onForgotPassword={() => setShowForgotPassword(true)}
        />
    );
};

export default LoginApp;
